import type { Tenant } from '../lib/database.types';
import { heroTitle, heroSubtitle } from '../lib/heroContent';

/** Emojis food décoratifs pour le fond de secours (sans photo) — purement visuels. */
const FALLBACK_EMOJIS = ['🍔', '🍟', '🌮', '🥙', '🥤', '🍕', '🌯', '🥗'];

/**
 * Hero / landing de la vitrine — porté À L'IDENTIQUE du legacy (Food Kourt / Big Burger / TFK).
 * Un seul template ; tout le contenu vient du tenant (base fait foi). RENDU INITIAL RICHE
 * même sans saisie admin (repli automatique) :
 *   - photo de fond  = hero_image_url (zoom lent ken-burns + voile sombre hero-grad) ;
 *     repli « fond de marque » (dégradé couleur resto + motif emoji food) si absente.
 *   - grand titre    = tagline (repli : « Votre commande {resto} prête à emporter. »).
 *   - sous-titre     = short_pitch (repli : dérivé des catégories du menu).
 *   - badge          = « Ouvert maintenant · Retrait en {prep} min ».
 *   - CTA primaire   = couleur de marque ; secondaire = verre dépoli.
 *   - notes Google / Uber : affichées seulement si > 0.
 *
 * `categoryNames` (facultatif) sert UNIQUEMENT au repli du sous-titre — passé par MenuView.
 */
export default function StoreHero({
  tenant,
  categoryNames = [],
}: {
  tenant: Tenant;
  categoryNames?: string[];
}) {
  const hasGoogle = (tenant.rating_google ?? 0) > 0;
  const hasUber = (tenant.rating_uber ?? 0) > 0;
  const prep = tenant.prep_time_min ?? 15;
  const heroImage = tenant.hero_image_url ?? null;
  // Repli automatique : si le resto n'a pas saisi son accroche / sous-titre, on génère
  // un texte attractif (jamais le nom seul sur fond noir).
  const title = heroTitle(tenant.tagline, tenant.name);
  const pitch = heroSubtitle(tenant.short_pitch, categoryNames);

  const scrollToMenu = () =>
    document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' });
  const scrollToInfos = () =>
    document.getElementById('infos')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section className="brand-hero relative min-h-[600px] overflow-hidden py-12 lg:min-h-[680px] lg:py-16">
      {/* Photo de fond du tenant (zoom lent + assombrie), si fournie. */}
      {heroImage ? (
        <div
          className="ken-burns absolute inset-0"
          style={{
            backgroundImage: `url('${heroImage}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.55)',
          }}
        />
      ) : (
        /* Repli sans photo : motif food discret, façon collage, aux couleurs de marque. */
        <div className="brand-hero-pattern absolute inset-0" aria-hidden="true">
          {FALLBACK_EMOJIS.map((e, i) => (
            <span
              key={i}
              className="brand-hero-emoji"
              style={{
                left: `${(i * 23 + 7) % 92}%`,
                top: `${(i * 37 + 11) % 80}%`,
                fontSize: `${56 + ((i * 17) % 64)}px`,
                animationDelay: `${i * 0.6}s`,
              }}
            >
              {e}
            </span>
          ))}
        </div>
      )}
      <div className="hero-grad absolute inset-0" />

      <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-center px-4 text-white lg:px-6">
        <div className="max-w-2xl">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/15 px-4 py-1.5 text-sm font-semibold backdrop-blur-md lg:mb-6">
            <span className="pulse-dot h-2 w-2 rounded-full bg-green-400" />
            Ouvert maintenant · Retrait en {prep} min
          </div>

          <h1 className="display-font mb-3 text-4xl font-black leading-[1.05] sm:text-5xl lg:mb-4 lg:text-6xl xl:text-7xl">
            {title}
          </h1>

          {pitch && <p className="mb-6 max-w-xl text-base opacity-90 lg:mb-8 lg:text-lg">{pitch}</p>}

          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              onClick={scrollToMenu}
              className="flex items-center justify-center gap-2 rounded-2xl px-8 py-4 text-lg font-bold text-white shadow-2xl transition active:scale-95"
              style={{ background: 'var(--brand-primary)' }}
            >
              Commander en ligne <span aria-hidden="true">→</span>
            </button>
            <button
              onClick={scrollToInfos}
              className="rounded-2xl border border-white/20 bg-white/15 px-8 py-4 text-lg font-bold backdrop-blur-md transition hover:bg-white/25"
            >
              Voir le restaurant
            </button>
          </div>

          {(hasGoogle || hasUber) && (
            <div className="mt-6 flex flex-wrap gap-3">
              {hasGoogle && (
                <div className="flex items-center gap-2 rounded-xl border border-white/20 bg-white/15 px-4 py-2 text-sm backdrop-blur">
                  <span className="text-yellow-300">★</span>
                  <span className="font-bold">{tenant.rating_google}/5</span>
                  <span className="opacity-80">
                    {(tenant.rating_google_count ?? 0) > 0
                      ? `— ${tenant.rating_google_count} avis Google`
                      : 'Google'}
                  </span>
                </div>
              )}
              {hasUber && (
                <div className="flex items-center gap-2 rounded-xl border border-white/20 bg-white/15 px-4 py-2 text-sm backdrop-blur">
                  <span className="text-yellow-300">★</span>
                  <span className="font-bold">{tenant.rating_uber}/5</span>
                  <span className="opacity-80">
                    {(tenant.rating_uber_count ?? 0) > 0
                      ? `— ${tenant.rating_uber_count} avis Uber Eats`
                      : 'Uber Eats'}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
